import { defineStore } from 'pinia'
import { apiBaseUrl } from '../runtime-config.mjs'
import { createPortalClient } from '../portal-api.mjs'

export const portalApi = createPortalClient(`${apiBaseUrl(window.location)}/auth/portal`)
const profileRequests = new WeakMap()

function requireCurrentSession(store, generation) {
    if (store.sessionGeneration !== generation) throw new Error('此操作已取消，请重试。')
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null, // { email, username, avatar, role }
        sessionGeneration: 0,
        profileStatus: null
    }),
    getters: {
        isAuthenticated: (state) => !!state.user
    },
    actions: {
        clearSession() {
            this.sessionGeneration += 1
            profileRequests.delete(this)
            this.user = null
        },
        async logout() {
            this.sessionGeneration += 1
            profileRequests.delete(this)
            const generation = this.sessionGeneration
            const response = await portalApi.post('/logout/')
            if (generation === this.sessionGeneration) this.clearSession()
            return response.data
        },
        async login(email, password) {
            const generation = ++this.sessionGeneration
            try {
                const response = await portalApi.post(`/login/`, {
                    email,
                    password
                })
                requireCurrentSession(this, generation)
                this.user = response.data.user
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '登录失败，请检查账号密码'
                throw new Error(msg)
            }
        },
        async sendLoginCode(email) {
            try {
                const response = await portalApi.post(`/send-login-code/`, { email })
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '发送验证码失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async loginWithCode(email, code) {
            const generation = ++this.sessionGeneration
            try {
                const response = await portalApi.post(`/code-login/`, {
                    email,
                    code
                })
                requireCurrentSession(this, generation)
                this.user = response.data.user
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '验证码登录失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async sendRegisterCode(email) {
            try {
                const response = await portalApi.post(`/send-code/`, { email })
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '发送验证码失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async sendPasswordResetCode(email) {
            try {
                const response = await portalApi.post(`/send-reset-code/`, { email })
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '发送验证码失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async resetPassword(email, code, password) {
            try {
                const response = await portalApi.post(`/reset-password/`, {
                    email,
                    code,
                    password
                })
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '密码重置失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async sendBindEmailCode(email) {
            if (!this.user) throw new Error('Not authenticated')
            try {
                const response = await portalApi.post(`/send-bind-code/`, { email })
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '发送验证码失败，请稍后再试'
                throw new Error(msg)
            }
        },
        async register(email, username, password, code, externalLogin = false) {
            const generation = externalLogin ? this.sessionGeneration : ++this.sessionGeneration
            try {
                const response = await portalApi.post(externalLogin ? '/register-only/' : '/register/', {
                    email,
                    username,
                    password,
                    code
                })
                requireCurrentSession(this, generation)
                if (!externalLogin) this.user = response.data.user
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '注册失败，请检查填写信息'
                throw new Error(msg)
            }
        },
        fetchProfile() {
            const generation = this.sessionGeneration
            const pending = profileRequests.get(this)
            if (pending?.generation === generation) return pending.promise
            const promise = (async () => {
                try {
                    const response = await portalApi.get(`/me/`)
                    if (generation === this.sessionGeneration) {
                        this.user = response.data
                        this.profileStatus = 200
                    }
                } catch (error) {
                    if (generation === this.sessionGeneration) this.profileStatus = error.response?.status || 503
                    if (generation === this.sessionGeneration && [401, 403].includes(error.response?.status)) this.clearSession()
                } finally {
                    if (profileRequests.get(this)?.generation === generation) profileRequests.delete(this)
                }
                return this.user
            })()
            profileRequests.set(this, { generation, promise })
            return promise
        },
        async updateProfile(profileData) {
            if (!this.user) throw new Error('Not authenticated')
            const generation = this.sessionGeneration

            const response = await portalApi.patch(`/me/`, profileData)
            requireCurrentSession(this, generation)
            this.user = response.data.user
            return response.data
        },
        async updateAvatar(avatar) {
            if (!this.user) throw new Error('Not authenticated')
            const generation = this.sessionGeneration
            const formData = new FormData()
            formData.append('avatar', avatar, 'avatar.webp')
            try {
                const response = await portalApi.put(`/me/avatar/`, formData)
                requireCurrentSession(this, generation)
                this.user = response.data.user
                return response.data
            } catch (error) {
                const msg = error.response?.data?.error || '头像上传失败，请重试'
                throw new Error(msg)
            }
        },
        async addEmail(email, code) {
            if (!this.user) throw new Error('Not authenticated')
            const generation = this.sessionGeneration
            const response = await portalApi.post(`/emails/`, { email, code })
            requireCurrentSession(this, generation)
            await this.fetchProfile()
            return response.data
        },
        async removeEmail(emailId) {
            if (!this.user) throw new Error('Not authenticated')
            const generation = this.sessionGeneration
            const response = await portalApi.delete(`/emails/${emailId}/`)
            requireCurrentSession(this, generation)
            await this.fetchProfile()
            return response.data
        }
    }
})
